/**
 * 화면 그룹 — 그룹을 만들고, 이름·그룹 편성을 고치고, 지운다. 그룹이 어디에 쓰이는지(화면·캠페인·담당 계정)도 줄마다.
 *
 * 그룹은 화면 묶음이다. 묶인 화면 중 **자기 편성이 없는 화면**에 그룹 편성이 나가고, 캠페인·긴급의 대상이 되고,
 * 사옥·지점 담당자의 담당 범위가 된다. 2026-10-19 에 만든 그룹 관리는 [화면] 의 [⋯] › 그룹 관리 창 안에 있었다 —
 * 편성·캠페인·사용자 셋이 그룹을 쓰는데 메뉴에서는 그룹이 있는지조차 보이지 않아, 찾는 사람이 없었다. 2026-10-21
 * 사이드바의 제 메뉴로 뗐다(화면 바로 아래).
 *
 * 화면을 넣고 빼는 일은 여기서 하지 않는다 — 화면 목록에서 화면을 골라 일괄 변경으로 한다. 거르고 고르는 도구가
 * 이미 거기 다 있고(위치·상태·검색), 여기에 화면 고르기를 또 만들면 같은 일을 하는 곳이 둘이 된다. 그래서 그룹의
 * 화면 수를 누르면 그 그룹으로 거른 화면 목록(`/displays?group=`)을 연다.
 *
 * 누구나 본다(그룹 목록은 읽기 권한). 이름·편성 고치기는 편성 권한(`schedule:write`), 만들기·지우기는 화면 관리 권한이
 * 있고 담당 범위가 없는 계정만 된다(서버 403) — 범위가 있는 사람이 만든 그룹은 만들자마자 그 사람의 범위 밖이 되고,
 * 지우면 그 그룹만 맡던 계정이 고객사 전체를 맡게 될 수 있다.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import type { Group, Me, SignageOneClient } from '../api/client.js';
import { can } from '../capabilities.js';
import { useAction, useAsync } from '../hooks.js';
import {
  assignmentFromKey,
  assignmentKey,
  assignmentName,
  blockerAdvice,
  groupDeleteBlockers,
  groupNameProblem,
  groupUsage,
  namesSummary,
  sortGroups,
  type GroupDeleteBlockers,
} from '../groups.js';
import { confirmDialog } from '../components/ConfirmDialog.js';
import { IconButton } from '../components/IconButton.js';
import { Empty, ErrorNote, ItemTitle, Loading, Panel } from '../components/ui.js';
import { assignmentKeyFromPick, PlaybackPicker, pickFromAssignmentKey } from '../components/PlaybackPicker.js';

export function GroupsScreen({ client, me }: { client: SignageOneClient; me: Me }): ReactNode {
  const navigate = useNavigate();
  /** 이름·그룹 편성을 고칠 수 있는가. */
  const canEdit = can(me, 'schedule:write');
  /** 만들기·지우기 — 화면 관리 권한이 있고 담당 범위가 없는 계정(서버도 403). */
  const canCreate = can(me, 'fleet:write') && !me.scope;
  /** 담당 계정 열 — 계정 목록은 계정 관리 권한이 있어야 받는다. 없으면 열을 그리지 않는다. */
  const canSeeUsers = can(me, 'user:manage');

  const groups = useAsync(() => client.listGroups(), [client]);
  const playlists = useAsync(() => client.allPlaylists(), [client]);
  const schedules = useAsync(() => client.listSchedules(), [client]);
  const campaigns = useAsync(() => client.listCampaigns(), [client]);
  const users = useAsync(() => (canSeeUsers ? client.listUsers() : Promise.resolve(null)), [client, canSeeUsers]);
  const action = useAction();
  const items = sortGroups(groups.data?.items ?? []);
  const playlistItems = playlists.data?.items ?? [];
  const scheduleItems = schedules.data?.items ?? [];
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string; assignment: string } | null>(null);
  /** 지우기를 막은 까닭 — 어느 그룹이었는지와 함께. */
  const [blocked, setBlocked] = useState<{ name: string; blockers: GroupDeleteBlockers } | null>(null);

  const done = (): void => {
    setEditing(null);
    groups.reload();
  };

  const createProblem = newName.trim() === '' ? null : groupNameProblem(newName, items, null);
  const create = (): void => {
    if (newName.trim() === '' || createProblem) return;
    const name = newName.trim();
    setBlocked(null);
    void action.run(
      () => client.createGroup({ name }),
      () => {
        setNewName('');
        done();
      },
      `'${name}' 그룹을 만들었습니다`,
    );
  };

  const save = (group: Group): void => {
    if (!editing) return;
    const name = editing.name.trim();
    const input: { name?: string; assignment?: Group['assignment'] } = {};
    if (name !== group.name) input.name = name;
    if (editing.assignment !== assignmentKey(group.assignment))
      input.assignment = assignmentFromKey(editing.assignment);
    if (Object.keys(input).length === 0) {
      setEditing(null);
      return;
    }
    setBlocked(null);
    void action.run(() => client.updateGroup(group.id, input), done, `'${name}' 그룹을 고쳤습니다`);
  };

  const remove = async (group: Group): Promise<void> => {
    const ok = await confirmDialog({
      title: '그룹 지우기',
      body: (
        <>
          <strong>{group.name}</strong> 그룹을 지웁니다. 화면·캠페인·계정이 쓰고 있으면 지워지지 않고, 무엇을 먼저
          치워야 하는지 알려 드립니다.
        </>
      ),
      confirmLabel: '지우기',
      danger: true,
    });
    if (!ok) return;
    setBlocked(null);
    void action.run(
      async () => {
        try {
          await client.deleteGroup(group.id);
          return true;
        } catch (cause) {
          // 쓰이고 있어 막힌 것은 오류 한 줄이 아니라 "먼저 할 일" 로 보인다.
          const blockers = groupDeleteBlockers(cause);
          if (!blockers) throw cause;
          setBlocked({ name: group.name, blockers });
          return false;
        }
      },
      (deleted) => {
        if (deleted) done();
      },
      (deleted) => (deleted ? `'${group.name}' 그룹을 지웠습니다` : null),
    );
  };

  const userItems = users.data?.items ?? null;
  const campaignItems = campaigns.data?.items ?? [];

  return (
    <div className="screen">
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
      {groups.error ? <ErrorNote message={groups.error} /> : null}

      <Panel
        title={`화면 그룹 ${items.length}개`}
        className="screen-fill"
        help="그룹은 화면 묶음입니다. 그룹 편성은 그룹의 화면 중 자기 편성이 없는 화면에 나가고, 캠페인·긴급 송출의 대상과 사옥·지점 담당자의 담당 범위로도 씁니다."
        actions={
          canCreate ? (
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                create();
              }}
            >
              <input
                className="input input-search"
                value={newName}
                maxLength={200}
                placeholder="새 그룹 이름 — 예: 판교 사옥"
                aria-label="새 그룹 이름"
                disabled={action.pending}
                onChange={(event) => setNewName(event.target.value)}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={action.pending || newName.trim() === '' || createProblem !== null}
                title={createProblem ?? undefined}
              >
                그룹 만들기
              </button>
            </form>
          ) : null
        }
      >
        {createProblem ? (
          <p className="field-error px-4 pt-3" role="alert">
            {createProblem}
          </p>
        ) : null}

        {/* 화면을 넣고 빼는 곳을 처음부터 말해 둔다 — 여기서 찾다가 못 찾고 그룹을 다시 만들지 않게. */}
        <p className="hint px-4 py-3">
          화면을 그룹에 넣고 빼려면 [화면] 목록에서 화면을 골라 [기타 일괄 변경] 의 그룹을 쓰세요. 그룹의 화면 수를
          누르면 그 그룹의 화면만 걸러 보여 줍니다.
        </p>

        {blocked ? (
          <div className="filter-note mt-3" role="alert">
            <span>
              <strong>{blocked.name}</strong> 그룹을 지울 수 없습니다.
              {blockerAdvice(blocked.blockers).map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </span>
            <button type="button" className="link" onClick={() => setBlocked(null)}>
              닫기
            </button>
          </div>
        ) : null}

        {groups.loading && !groups.data ? <Loading /> : null}

        {groups.data && items.length === 0 ? (
          <Empty
            title={canCreate || !me.scope ? '화면 그룹이 없습니다' : '맡은 그룹이 없습니다'}
            hint={
              canCreate
                ? '사옥·지점처럼 담당자나 편성이 갈리는 단위로 위에서 만드세요.'
                : '그룹 만들기는 화면 관리 권한이 있고 담당 범위가 없는 관리자가 합니다.'
            }
          />
        ) : null}

        {items.length > 0 ? (
          <div className="table-scroll">
            <table className="table min-w-[44rem]">
              <thead>
                <tr>
                  <th>그룹</th>
                  <th>화면</th>
                  <th>그룹 편성</th>
                  <th>캠페인 대상</th>
                  {userItems ? <th>담당 계정</th> : null}
                  <th className="col-actions" aria-label="동작" />
                </tr>
              </thead>
              <tbody>
                {items.map((group) => {
                  const isEditing = editing?.id === group.id;
                  const problem = isEditing ? groupNameProblem(editing.name, items, group.id) : null;
                  const planned = assignmentName(group.assignment, playlistItems, scheduleItems);
                  const usage = groupUsage(group.id, campaignItems, userItems);
                  return (
                    <tr key={group.id}>
                      <td className="cell-strong">
                        {isEditing ? (
                          <input
                            className="input input-sm w-full"
                            value={editing.name}
                            maxLength={200}
                            aria-label={`${group.name} 새 이름`}
                            autoFocus
                            disabled={action.pending}
                            onChange={(event) => setEditing({ ...editing, name: event.target.value })}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' && !problem) save(group);
                              if (event.key === 'Escape') setEditing(null);
                            }}
                          />
                        ) : (
                          // 이름을 누르면 [고치기] 와 같다 — 이름·배정을 그 줄에서 고친다.
                          <ItemTitle
                            onOpen={
                              canEdit && !action.pending
                                ? () =>
                                    setEditing({
                                      id: group.id,
                                      name: group.name,
                                      assignment: assignmentKey(group.assignment),
                                    })
                                : undefined
                            }
                            label={`${group.name} 고치기`}
                          >
                            {group.name}
                          </ItemTitle>
                        )}
                        {problem ? <small className="field-error block">{problem}</small> : null}
                      </td>
                      <td className="whitespace-nowrap">
                        {group.display_count > 0 ? (
                          <button
                            type="button"
                            className="link"
                            title="이 그룹의 화면을 화면 목록에서 봅니다"
                            onClick={() => navigate(`/displays?group=${encodeURIComponent(group.id)}`)}
                          >
                            {group.display_count}대
                          </button>
                        ) : (
                          <span className="cell-muted">0대</span>
                        )}
                      </td>
                      <td className="min-w-[12rem]">
                        {isEditing ? (
                          <PlaybackPicker
                            label={`${group.name} 그룹 편성`}
                            none="그룹 편성 없음"
                            value={pickFromAssignmentKey(editing.assignment)}
                            sources={{ playlists: playlistItems, schedules: scheduleItems }}
                            disabled={action.pending}
                            onChange={(next) => setEditing({ ...editing, assignment: assignmentKeyFromPick(next) })}
                          />
                        ) : planned ? (
                          planned
                        ) : (
                          <span className="cell-muted">없음</span>
                        )}
                      </td>
                      <td className="cell-muted" title={usage.campaigns.join(', ') || undefined}>
                        {usage.campaigns.length > 0 ? namesSummary(usage.campaigns, '개') : '—'}
                      </td>
                      {usage.users ? (
                        <td className="cell-muted" title={usage.users.join(', ') || undefined}>
                          {usage.users.length > 0 ? namesSummary(usage.users, '명') : '—'}
                        </td>
                      ) : null}
                      <td className="col-actions">
                        {isEditing ? (
                          <span className="inline-flex gap-1.5">
                            <button
                              type="button"
                              className="btn btn-tiny btn-primary"
                              disabled={action.pending || problem !== null}
                              title={problem ?? undefined}
                              onClick={() => save(group)}
                            >
                              저장
                            </button>
                            <button
                              type="button"
                              className="btn btn-tiny"
                              disabled={action.pending}
                              onClick={() => setEditing(null)}
                            >
                              취소
                            </button>
                          </span>
                        ) : (
                          <>
                            {canEdit ? (
                              <IconButton
                                icon={Pencil}
                                label="고치기"
                                subject={group.name}
                                disabled={action.pending}
                                onClick={() =>
                                  setEditing({
                                    id: group.id,
                                    name: group.name,
                                    assignment: assignmentKey(group.assignment),
                                  })
                                }
                              />
                            ) : null}
                            {canCreate ? (
                              <IconButton
                                icon={Trash2}
                                label="지우기"
                                subject={group.name}
                                danger
                                disabled={action.pending}
                                onClick={() => void remove(group)}
                              />
                            ) : null}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </Panel>
    </div>
  );
}
